/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { FC } from "react"
import { Select } from "@duxton-web/ui"
import { useDispatch, useSelector } from "react-redux"
import { useLocation } from "react-router-dom"
import { AppState } from "store/store.type"
import { Item } from "types/models/menu"
import {
  AvailableStatusesText,
  availableStatuses,
  availableStatusesStyle,
  getAvailableStatusesNonRecurring,
  getAvailableStatusesRecurring,
  availableStatusesValue,
  isRecurringItem,
  MarkStatusesText,
  useItemWithBackTimeMessageText,
  availableStatusesJustShow,
  availableStatusesUsage,
} from "utils/item_helper"
import { useMenuEditorStatus } from "pages/menu/detail/hooks/useMenuEditorStatus"
import {
  TroyWebTracker,
  getResponseErrorEventLabels,
  getResponseErrorEventLabelsForRTK,
} from "utils/tracking"
import { EventAction, EventCategory, EventEntryPoint } from "constants/tracking"
import { menuApi, useLazyFetchMenuQuery } from "queries/menuApi"
import { CACHE_QUERY_TAGS } from "queries/constants"
import useRtkQueryMigrationEndpointsFlag from "hooks/useRtkQueryMigrationEndpointsFlag"
import { useEnablePartialMenu } from "hooks/useEnablePartialMenu"
import { useUpdateItemsAvailableV1Mutation } from "queries"
import { updateItemsAvailableStatus } from "utils/rtk_migration_helper"
import "./ItemAvailabilityDropdown.less"
import dayjs from "dayjs"

interface ItemAvailabilityDropdownProps {
  item: Item
}

export const ItemAvailabilityDropdown: FC<ItemAvailabilityDropdownProps> = ({
  item,
}: ItemAvailabilityDropdownProps) => {
  const location = useLocation()
  const eventCategory = location.pathname.endsWith("/items")
    ? EventCategory.menuItemsTab
    : EventCategory.menuOverviewTab

  const dispatch = useDispatch()
  const { isStatusChangeAllowed } = useMenuEditorStatus()
  const {
    merchantID,
    isMartDepartmentStructure,
    isSellingTimeStructure,
    merchantGroupID,
    menuGroupDetail: { menuGroupID },
  } = useSelector((state: AppState) => state?.menu)

  const {
    flags: {
      updateItemsAvailableStatus: isRtkUpdateItemsAvailableStatusEnabled,
      fetchMenu: isRtkFetchMenuEnabled,
    },
    isLoading: isRtkFlagLoading,
  } = useRtkQueryMigrationEndpointsFlag()

  const enablePartialMenu = useEnablePartialMenu()
  const [updateItemsAvailableV1] = useUpdateItemsAvailableV1Mutation()
  const [fetchMenuRtk] = useLazyFetchMenuQuery()

  const { availableStatus } = item

  const statuses = isRecurringItem(item)
    ? getAvailableStatusesRecurring()
    : getAvailableStatusesNonRecurring()

  const availableStatusesText = AvailableStatusesText(
    availableStatusesUsage.ITEM_SELECTOR,
  )
  const availableUntilText = useItemWithBackTimeMessageText(
    dayjs(item.availableAt),
  )

  const markStatusesTextSingular = MarkStatusesText(false)

  const getCategoryLabels = (updatedItem: Item) =>
    isMartDepartmentStructure
      ? {
          CATEGORY_ID: updatedItem.departmentID,
          SUB_CATEGORY_ID: updatedItem.subDepartmentID,
        }
      : { CATEGORY_ID: updatedItem.categoryID }

  const handleChange = async (value: number) => {
    if (!item?.itemID) {
      return
    }

    const result = await updateItemsAvailableStatus({
      dispatch,
      merchantID,
      merchantGroupID,
      menuGroupID,
      isRtkFlagLoading,
      isRtkUpdateItemsAvailableStatusEnabled,
      isRtkFetchMenuEnabled,
      fetchMenuRtk,
      updateItemsAvailableV1Rtk: updateItemsAvailableV1,
      itemIDs: [item.itemID],
      availableStatus: value,
      successMessage: markStatusesTextSingular[value],
      isMartDepartmentStructure,
      isSellingTimeStructure,
      enablePartialMenu,
      invalidateTagsAction: menuApi.util.invalidateTags([
        CACHE_QUERY_TAGS.martItemMenuDisplayV2,
      ]),
    })

    let labels = {
      STORE_ID: merchantID,
      ENTRY_POINT: EventEntryPoint.availabilityDropdown,
      ITEM_ID: item.itemID,
      NO_ITEM_SELECTED: 1,
      VALUE: availableStatusesValue[value],
      STATUS: result.succeeded ? "SUCCESS" : "FAILURE",
      REASON: "",
      ERROR_MSG: "",
      ...getCategoryLabels(item),
    }
    if (!result.succeeded) {
      if (result.rtkError) {
        labels = {
          ...labels,
          ...getResponseErrorEventLabelsForRTK({ error: result.rtkError }),
        }
      } else if (result.legacyResp) {
        labels = {
          ...labels,
          ...getResponseErrorEventLabels(result.legacyResp),
        }
      }
    }
    TroyWebTracker.sendEvent({
      category: eventCategory ?? EventCategory.menuOverviewTab,
      action: EventAction.submitItemAvailability,
      labels,
    })
  }

  const renderJustShowStatuses = () => {
    let selectorText = ""
    if (availableStatus === availableStatuses.UNAVAILABLE_UNTIL_CUSTOM) {
      selectorText = availableUntilText
    } else {
      selectorText = availableStatusesText[availableStatus as number]
    }

    return (
      availableStatusesJustShow.includes(availableStatus as number) && (
        <Select.Option
          value={availableStatus}
          key={availableStatus}
          style={availableStatusesStyle[availableStatus as number] ?? {}}
          className="hidden-option"
          title={selectorText}
        >
          {selectorText}
        </Select.Option>
      )
    )
  }

  return (
    <div
      onClick={e => {
        e.stopPropagation()
      }}
    >
      <Select
        css={css`
          width: 100%;
        `}
        size="small"
        style={
          availableStatusesStyle[
            item?.availableStatus ?? availableStatuses.AVAILABLE
          ] ?? {}
        }
        value={Number(item.availableStatus)}
        onChange={handleChange}
        popupClassName="item-availability-dropdown"
        data-testid="item-availability-dropdown"
        disabled={!isStatusChangeAllowed || isRtkFlagLoading}
      >
        {statuses.map(status => (
          <Select.Option
            key={status}
            value={Number(status)}
            style={
              availableStatusesStyle[availableStatusesUsage.ITEM_SELECTOR][
                status
              ] ?? {}
            }
            title={availableStatusesText[Number(status)]}
          >
            {availableStatusesText[Number(status)]}
          </Select.Option>
        ))}
        {renderJustShowStatuses()}
      </Select>
    </div>
  )
}
